<template>
  <div v-if="theme === 'theme3'" class="tw-mt-2 tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-10 lg:tw-gap-y-12">
    <nuxt-link
      v-for="(property, index) in properties"
      :key="`property-${index}`"
      :external="true"
      :to="property.detail_page_url"
      class="d-flex flex-column mb-3 mb-md-0 n-cursor-pointer property-card tw-col-span-12 tw-border-b tw-border-[var(--default-border-color)] md:tw-col-span-6 xl:tw-col-span-4"
    >
      <lazy-theme3-properties-property-list-item
        :property="property"
        :is-letter-property-criteria="isLetterPropertyCriteria"
        :pre-market-blur-text="preMarketBlurText"
      />
    </nuxt-link>
  </div>

  <div
    v-else-if="theme === 'theme4'"
    class="tw-mt-5 tw-grid tw-grid-cols-1 tw-gap-4"
    :class="{
      'xl:tw-grid-cols-2': columns === 2,
      'lg:tw-grid-cols-2 xl:tw-grid-cols-3': columns === 3,
      'xl:tw-grid-cols-4': columns === 4,
    }"
  >
    <div
      v-for="(property, index) in properties"
      :key="`property-${index}`"
      :external="true"
      :to="property.detail_page_url"
      class="tw-grid-cols-2 !tw-p-[unset] lg:tw-grid-cols-1"
      @click="property.featured_video ? setProperty(property.featured_video) : null"
    >
      <lazy-theme4-properties-property-list-item
        :property="property"
        :is-letter-property-criteria="isLetterPropertyCriteria"
        version="v2"
        :preMarketPropertyFeature="preMarketPropertyFeature"
        :pre-market-blur-text="preMarketBlurText"
        @open-video-popup="isVisible = true"
      />
    </div>

    <library-popups-video-popup
      v-if="selectedPropertyVideo"
      :videoUrl="selectedPropertyVideo"
      v-model:is-visible="isVisible"
      :portrait="true"
    />
  </div>

  <div
    v-else-if="theme === 'theme6'"
    class="tw-mt-5 tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-2"
    :class="{ 'lg:!tw-grid-cols-1': columns === 1 }"
  >
    <div
      v-for="(property, index) in properties"
      :key="`property-${index}`"
      class="tw-grid-cols-2 !tw-p-[unset] lg:tw-grid-cols-1"
      :class="{ '!tw-grid-cols-1 lg:!tw-grid-cols-1': columns === 1 }"
    >
      <lazy-theme6-properties-property-list-item
        :property="property"
        :is-letter-property-criteria="isLetterPropertyCriteria"
        :version="version"
        :pre-market-blur-text="preMarketBlurText"
        :preMarketPropertyFeature="preMarketPropertyFeature"
      />
    </div>
  </div>

  <div v-else-if="theme === 'theme8'" class="properties-page-list tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-2">
    <template v-for="(property, propertyIndex) in properties">
      <nuxt-link
        :external="true"
        :to="property.detail_page_url"
        class="n-link tw-grid-cols-2 !tw-p-[unset] lg:tw-grid-cols-1"
      >
        <component
          :is="`${theme}-property-list-item`"
          :index="propertyIndex"
          :property="property"
          :is-letter-property-criteria="isLetterPropertyCriteria"
          :version="version"
        />
      </nuxt-link>
      <template v-for="(customcard, index) in cards" :key="`custom-card-${index}`">
        <div
          v-if="customcard.index === propertyIndex"
          :style="`background-color: var(--c-${customcard.background}); color: var(--c-${customcard.text})`"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-4 tw-text-center"
        >
          <h6 v-if="customcard.alt_title" class="tw-mb-4 tw-uppercase tw-text-[var(--c-secondary)]">
            {{ customcard.alt_title }}
          </h6>
          <h3 v-if="customcard.title" class="tw-max-w-[350px] tw-uppercase">{{ customcard.title }}</h3>
          <p v-if="customcard.description" class="tw-mt-10 tw-max-w-[350px] tw-text-center">
            {{ customcard.description }}
          </p>
          <nuxt-link
            v-if="customcard.button_label"
            :to="customcard.button_link"
            class="tw-mt-10"
            :class="`n-${customcard.button_type}`"
          >
            {{ customcard.button_label }}
          </nuxt-link>
        </div>
      </template>
    </template>
  </div>

  <transition name="fade" mode="out-in" v-else-if="theme === 'theme9'">
    <div
      class="properties-page-list tw-grid tw-grid-cols-1 tw-gap-4"
      :class="{
        'xl:tw-grid-cols-2': columns === 2,
        'lg:tw-grid-cols-2 xl:tw-grid-cols-3': columns === 3,
        'xl:tw-grid-cols-4': columns === 4,
        'lg:!tw-grid-cols-1': !isGridView,
        'xl:!tw-grid-cols-1': !isGridView,
      }"
      :key="isGridView"
    >
      <nuxt-link
        v-for="(property, index) in properties"
        :key="`property-${index}`"
        :external="true"
        :to="property.detail_page_url"
        class="n-link property-item tw-grid-cols-2 !tw-p-[unset] lg:tw-grid-cols-1"
      >
        <component
          :is="`${theme}-property-list-item`"
          :index="index"
          :property="property"
          :is-letter-property-criteria="isLetterPropertyCriteria"
          :pre-market-blur-text="preMarketBlurText"
          :isVertical="isGridView"
          :version="version"
        />
      </nuxt-link>
    </div>
  </transition>

  <div v-else class="properties-page-list tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-2"
  :class="{
    'lg:tw-grid-cols-3': columns === 3,
    'lg:tw-grid-cols-4': columns === 4,
  }">
    <nuxt-link
      v-for="(property, index) in properties"
      :key="`property-${index}`"
      :external="true"
      :to="property.detail_page_url"
      class="n-link property-item tw-grid-cols-2 !tw-p-[unset] lg:tw-grid-cols-1"
    >
      <component
        :is="`${theme}-property-list-item`"
        :index="index"
        :property="property"
        :is-letter-property-criteria="isLetterPropertyCriteria"
        :pre-market-blur-text="preMarketBlurText"
        :version="version"
      />
    </nuxt-link>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { mapState } from 'pinia';
import { ComponentMixin } from '~/mixins/component.mixin';
import type { PropertyItemComponent } from '~/units/properties/types';
import { useWebsiteStore } from '~/units/website/store';
import NcTooltip from '~/components/common/NcTooltip.vue';
import PropertyListItem from '~/components/theme3/properties/PropertyListItem.vue';
import Theme4PropertyListItem from '~/components/theme3/properties/PropertyListItem.vue';
import Theme5PropertyListItem from '~/components/theme5/properties/PropertyListItem.vue';
import Theme7PropertyListItem from '~/components/theme7/properties/PropertyListItem.vue';
import Theme8PropertyListItem from '~/components/theme8/properties/PropertyListItem.vue';
import Theme9PropertyListItem from '~/components/theme9/properties/PropertyListItem.vue';
export default defineNuxtComponent({
  name: 'PropertiesPageList',
  components: {
    PropertyListItem,
    NcTooltip,
    Theme4PropertyListItem,
    Theme5PropertyListItem,
    Theme7PropertyListItem,
    Theme8PropertyListItem,
    Theme9PropertyListItem,
  },

  mixins: [ComponentMixin],

  props: {
    properties: {
      required: true,
      type: Array as PropType<PropertyItemComponent[]>,
    },

    columns: {
      required: false,
      type: Number,
      default: 2,
    },

    cards: {
      required: false,
    },

    preMarketPropertyFeature: {
      required: false,
      type: Boolean,
      default: false,
    },

    version: {
      required: false,
      type: String,
      default: 'v1',
    },

    preMarketBlurText: {
      required: false,
      type: String,
    },

    isGridView: {
      required: false,
      type: Boolean,
    },
  },

  data() {
    return {
      isVisible: false,
      selectedPropertyVideo: null,
    };
  },

  computed: {
    ...mapState(useWebsiteStore, ['theme']),

    isLetterPropertyCriteria() {
      return !!this.vars.isLetterPropertyCriteriaNumber;
    },
  },

  methods: {
    setProperty(property_video: string) {
      if (property_video.mediaLink.includes('youtube') || property_video.mediaLink.includes('you.tube')) {
        // Regular expression to match the video ID in various YouTube URL formats, including Shorts
        const regExp = /^.*(youtu.be\/|v\/|\/u\/\w\/|embed\/|watch\?v=|\&v=|watch\?vi=|shorts\/)([^#\&\?]*).*/;
        const match = property_video.mediaLink.match(regExp);
        if (match && match[2].length === 11) {
          this.selectedPropertyVideo = `https://www.youtube.com/embed/${match[2]}`;
          return match[2];
        } else {
          return null;
        }
      }
    },
  },
});
</script>

<style scoped>
.property-card {
  transition:
    transform 0.3s linear,
    -webkit-transform 0.3s linear;
}
.property-card:hover {
  -webkit-transform: scale(1.02);
  transform: scale(1.02);
  z-index: 999;
}
</style>
